import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, Button, Tooltip, IconButton } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarIcon from "@mui/icons-material/Star";
import TemplateSelector from "./TemplateSelector";
import AIReplyAssistant from "./AIReplyAssistant";
import { useTemplateLibrary } from "./useTemplateLibrary";
import {
  IEmailTemplate, IAIDraftReplyResult,
  renderTemplateForLead,
} from "../../Api/emailTemplatesApi";

const NAVY = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_PALE = "#FBF7EE";
const HAIRLINE = "rgba(10,14,26,0.06)";

/** Categories surfaced as quick chips above the editor. Order is the
 *  one staff scan fastest by. Each chip applies the DEFAULT active
 *  template for that category (or the first active one if none is
 *  flagged as default). */
const QUICK_CATEGORIES: { key: string; label: string }[] = [
  { key: "new_inquiry_reply",    label: "Inquiry Reply" },
  { key: "follow_up",            label: "Follow-up" },
  { key: "quote_sent",           label: "Quote Sent" },
  { key: "payment_reminder",     label: "Reminder" },
  { key: "booking_confirmation", label: "Booking Confirmation" },
  { key: "thank_you",            label: "Thank You" },
];

export interface AppliedTemplate {
  /** Template id used (for tracking on send). */
  template_id: number;
  /** Subject after variable resolution. */
  subject: string;
  /** Body after variable resolution. */
  body: string;
}

export interface EmailComposerToolbarProps {
  /** Optional: thread context for the AI Draft assistant. */
  threadId?: number | null;
  /** Optional: lead context — drives variable resolution AND the
   *  AI Draft assistant's lead-context payload. When omitted, the
   *  resolver paints sample values from the variable schema. */
  leadId?: number | null;
  /** Current subject in the parent composer — fed to the AI Draft
   *  assistant for context. */
  subject?: string;
  /** True when the body has non-empty content — gates the
   *  "Replace existing body?" confirmation in both child modals. */
  hasExistingBody: boolean;
  /** Called when the rep applies a template. The parent should set
   *  subject + body and persist template_id alongside the send. */
  onApplyTemplate: (applied: AppliedTemplate) => void;
  /** Called when the rep applies an AI draft. The parent should set
   *  the body and mark ai_assisted=true on the next send. */
  onApplyAIDraft: (result: IAIDraftReplyResult) => void;
}

/**
 * Shared composer toolbar — sits above the message body in both
 * the Email Hub Reply modal and the Lead → Send Email panel.
 * One implementation, identical experience across surfaces.
 *
 *   [Use Template ▼]  [✨ AI Draft]  · Quick chips: Inquiry Reply, Follow-up, Quote Sent, …
 *
 * Variable resolution happens server-side via /render-for-lead when
 * a lead is in scope; otherwise the resolver paints sample values
 * so {{tokens}} never reach the editor as raw placeholders.
 */
const EmailComposerToolbar: React.FC<EmailComposerToolbarProps> = ({
  threadId, leadId, subject, hasExistingBody, onApplyTemplate, onApplyAIDraft,
}) => {
  // Hard-scope to email — Phase 2 added activity template types and we
  // don't want a "Quote Discussion" call-log template appearing in the
  // email composer's picker.
  const lib = useTemplateLibrary({ type: "email" });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [aiOpen, setAiOpen]         = useState(false);
  const [applying, setApplying]     = useState(false);

  // Map of category → default-or-first active template, used by the
  // quick chips so one click applies the right template per category.
  const chipTemplateByCategory = useMemo(() => {
    const m = new Map<string, IEmailTemplate>();
    for (const t of lib.templates) {
      if (!t.category) continue;
      const existing = m.get(t.category);
      if (!existing) m.set(t.category, t);
      else if (t.is_default && !existing.is_default) m.set(t.category, t);
    }
    return m;
  }, [lib.templates]);

  const applyTemplate = async (template: IEmailTemplate) => {
    if (hasExistingBody) {
      const ok = window.confirm(`Replace the current draft with template "${template.name}"?`);
      if (!ok) return;
    }
    setApplying(true);
    try {
      let subjectOut = template.subject;
      let bodyOut    = template.body;
      // When we have a lead, render with the resolver so
      // {{tokens}} land already-substituted in the editor.
      if (leadId) {
        try {
          const rendered = await renderTemplateForLead(template.id, leadId);
          subjectOut = rendered.subject;
          bodyOut    = rendered.body;
        } catch {
          // Fall back to the raw template — better than blocking the
          // workflow on a resolver hiccup. The unresolved {{tokens}}
          // are visibly present so the rep can spot them.
        }
      }
      lib.markUsed(template.id);
      onApplyTemplate({ template_id: template.id, subject: subjectOut, body: bodyOut });
      setPickerOpen(false);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box sx={{
      p: 1, borderRadius: "10px",
      bgcolor: "#fff",
      border: `1px solid ${HAIRLINE}`,
    }}>
      <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          onClick={() => setPickerOpen(true)}
          startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
          disabled={lib.loading || applying}
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 11.5, color: NAVY,
            border: `1px solid ${HAIRLINE}`,
            bgcolor: "#fafbfc",
            px: 1.2, py: 0.35, borderRadius: "8px",
            "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
          }}
        >
          Use Template
        </Button>

        <Button
          size="small"
          onClick={() => setAiOpen(true)}
          startIcon={<AutoAwesomeIcon sx={{ fontSize: 16, color: CHAMPAGNE }} />}
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 11.5, color: NAVY,
            border: `1px solid rgba(201,169,97,0.45)`,
            bgcolor: CHAMPAGNE_PALE,
            px: 1.2, py: 0.35, borderRadius: "8px",
            "&:hover": { bgcolor: "#FBF0D7", borderColor: CHAMPAGNE },
          }}
        >
          AI Draft
        </Button>

        {lib.favorites.length > 0 && (
          <Tooltip title="Open Favorites" arrow>
            <IconButton
              size="small"
              onClick={() => setPickerOpen(true)}
              sx={{
                color: CHAMPAGNE,
                border: `1px solid ${HAIRLINE}`,
                bgcolor: "#fafbfc",
                width: 28, height: 28,
                "&:hover": { bgcolor: CHAMPAGNE_PALE },
              }}
            >
              <StarIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />
      </Stack>

      {/* ── Quick category chips — one click applies the default
          template for that category. Only chips that actually have a
          template loaded are rendered. */}
      {lib.templates.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap mt={1}>
          <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", mr: 0.4, mt: 0.4 }}>
            QUICK:
          </Typography>
          {QUICK_CATEGORIES.map(cat => {
            const t = chipTemplateByCategory.get(cat.key);
            if (!t) return null;
            return (
              <Tooltip key={cat.key} title={t.name} arrow>
                <Box
                  role="button"
                  onClick={() => applyTemplate(t)}
                  sx={{
                    cursor: "pointer", userSelect: "none",
                    px: 1, py: 0.3, borderRadius: "999px",
                    fontSize: 10.5, fontWeight: 600,
                    color: NAVY,
                    bgcolor: "#fafbfc",
                    border: `1px solid ${HAIRLINE}`,
                    "&:hover": { bgcolor: CHAMPAGNE_PALE, borderColor: CHAMPAGNE },
                  }}
                >
                  {cat.label}
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      )}

      {pickerOpen && (
        <TemplateSelector
          open={pickerOpen}
          templates={lib.templates}
          categories={lib.categories}
          favorites={lib.favorites}
          recents={lib.recents}
          onToggleFavorite={lib.toggleFavorite}
          onApply={applyTemplate}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {aiOpen && (
        <AIReplyAssistant
          threadId={threadId ?? null}
          leadId={leadId ?? null}
          subject={subject}
          hasExistingBody={hasExistingBody}
          onClose={() => setAiOpen(false)}
          onApply={(result) => { onApplyAIDraft(result); setAiOpen(false); }}
        />
      )}
    </Box>
  );
};

export default EmailComposerToolbar;
